import { createContext, useContext, useId } from "react";

/**
 * Minimal RadioGroup primitive backed by native `<input type="radio">`.
 *
 * Mirrors the @radix-ui/react-radio-group API surface (`value`, `onValueChange`,
 * `disabled`, `<RadioGroup>` + `<RadioGroupItem>`) so a future swap to the
 * Radix package is mechanical. We use native radio inputs to avoid pulling in
 * another Radix dep for one screen.
 */

const RadioGroupContext = createContext(null);

export function RadioGroup({
  value,
  onValueChange,
  disabled = false,
  name,
  className,
  children,
  ...rest
}) {
  const generatedName = useId();
  const ctx = {
    value,
    onValueChange,
    disabled,
    name: name || generatedName,
  };
  return (
    <RadioGroupContext.Provider value={ctx}>
      <div role="radiogroup" className={className} {...rest}>
        {children}
      </div>
    </RadioGroupContext.Provider>
  );
}

export function RadioGroupItem({ value, id, className, ...rest }) {
  const ctx = useContext(RadioGroupContext);
  if (!ctx) {
    throw new Error("RadioGroupItem must be rendered inside a <RadioGroup>");
  }
  const checked = ctx.value === value;
  return (
    <input
      type="radio"
      id={id}
      name={ctx.name}
      value={value}
      checked={checked}
      disabled={ctx.disabled}
      onChange={() => {
        if (!ctx.disabled) ctx.onValueChange?.(value);
      }}
      className={className}
      {...rest}
    />
  );
}
